import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { useContext, useEffect, useState } from 'react';
import { error as errorAlert, infoAlert, successAlert, warningAlert } from '../../components/toastr';
import { SubmitButton, CancelButton } from '../../components/btns';
import { DateSelector } from '../../components/datetimepicker';
import moment from 'moment';
import SelectInput, { Select } from '../../components/select';
import cur, { UGX } from '../../components/currency';
import { createPayment, updatePayment } from '../../resources/api/bill-payments';
import { useNavigate } from 'react-router-dom';
import { paymentMethods } from '../../assets/constants';
import { UCWords } from '../../components/resources';
import { getAccounts } from '../../resources/api/accounts';
import { AppUserContext } from '../../App';
import { getVendors } from '../../resources/api/vendors';
import { getBills } from '../../resources/api/bills';
import FileInput from '../../components/files';

const { Control, Control: { Feedback }, Label } = Form;

/**
 * handle page for creating payment
 * @param {Object} props
 * @param {"edit"|"create"} props.action
 * @param {import('../../resources/api/bill-payments').PaymentObject} props.details
 * @param {(details: import('../../resources/api/bill-payments').PaymentObject) => void} props.onSubmit
 */
const PaymentForm = ({ details: paymentObject, action, onSubmit }) => {

    const { profile } = useContext(AppUserContext);
    const [details, setDetails] = useState(paymentObject);
    const [prods, setProds] = useState(paymentObject.items);
    const [vendors, setVendors] = useState([]);
    const [bills, setBills] = useState([]);

    const [deleted_prods, setDeletedProds] = useState([]);
    const [selected_bills, setSelectedBills] = useState([]);
    const [display_bills, setDisplayBills] = useState([]);
    const [accounts, setAccounts] = useState([]);

    const [validated, setValidated] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);


    useEffect(() => {

        setDisplayBills(
            bills.filter(i => (selected_bills.indexOf(i.id) === -1))
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selected_bills), JSON.stringify(bills)])

    const navigate = useNavigate();

    const changeProdAmount = (amount, idx) => {
        const _prods = [...prods];

        let _prod = _prods[idx];
        _prod = { ..._prod, amount, hasChanged: 1 };
        _prods.splice(idx, 1, _prod);
        setProds(_prods);
    }

    const deleteProd = idx => {
        const _prod = [...prods][idx];
        setProds(prods => prods.filter((e, i) => i !== idx));
        setSelectedBills(ids => ids.filter(i => (i !== _prod.invoice_id)));
        if (!!_prod.id) setDeletedProds(p => p.concat(_prod.id));

    }

    const handleBillSelect = ids => {
        let items = bills
            .filter(i => ids.indexOf(i.id) !== -1)
            .map(i => ({
                bill_id: i.id,
                bill_reference: i.vendor_reference,
                bill_balance: i.balance,
                amount: 0,
                isNew: true
            }))

        setSelectedBills(ids => [...ids, ...items.map(i => i.invoice_id)]);
        setProds(prev => ([...prev, ...items]));
    }

    /**
     * handle the overall submitting of the form
     * @param {React.FormEvent} e
     */
    const handleSubmit = e => {
        const form = e.currentTarget;

        e.preventDefault();

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("You have errors in your form. These have been highlighted for you.", "Form Errors");
            return;
        }

        setValidated(false);
        setSubmitting(true);

        let promise;

        if (action === "create") {
            promise = createPayment({
                ...details,
                items: prods.filter(p => !!parseInt(p.amount))
            });
        } else {
            promise = updatePayment({
                ...details,
                new_items: prods.filter(p => (p.isNew && !!parseInt(p.amount))),
                updated_items: prods.filter(p => (!!p.id && p.hasChanged)),
                deleted_items: deleted_prods
            }, details.id);
        }

        promise
            .then(({ payment, message }) => {
                successAlert(message);
                onSubmit(payment);
            })
            .catch(e => {
                errorAlert(e);
                setSubmitting(false);
            })
    }

    // get clients and accounts on load of the form
    useEffect(() => {

        Promise.all([getVendors(1), getAccounts()])
            .then(([{ vendors }, { accounts }]) => {
                setVendors(
                    vendors
                        // .filter(e => (e.balance > 0))
                        .map(e => ({
                            ...e,
                            description: e.address
                        })));

                setAccounts(accounts.filter(a => a.isActive));
            })

            .catch(e => errorAlert(e));
    }, [])

    //get unpaid/partially-paid invoices for  a selected client
    useEffect(() => {

        setBills([]);
        setProds([]);


        if (!details.vendor_id) return;

        infoAlert("Loading bills for this vendor...")

        getBills(true, { vendor_id: details.vendor_id, status: ['unpaid', 'partially-paid', 'overdue'] })
            .then(({ bills }) => {
                setBills(bills.map(i => ({
                    ...i,
                    title: i.vendor_reference,
                    description: `${moment(i.bill_date).format("DD MMM YYYY")} | ${UGX(i.total_amount).format()}`
                })));

                if (bills.length > 0)
                    successAlert("Successfully loaded all vendor's invoices")
                else
                    warningAlert("Vendor has no unpaid bills");
            })
            .catch(e => errorAlert(`Could not get unpaid bills for the selected vendor. ${e}`));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details.vendor_id])

    useEffect(() => {

        if (action === "edit") {
            setProds(paymentObject.items.map(p => ({ ...p, bill_balance: p.amount + p.bill_balance })))
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action])


    return (
        <Form className="max-800" validated={validated} noValidate onSubmit={handleSubmit}>
            <h4 className="form-section-label">Details</h4>
            <Row className="g-2">
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Vendor</Label>

                    {(action === "create") &&
                        <SelectInput
                            value={[details.vendor_id]}
                            onChange={([id]) => setDetails(d => ({
                                ...d,
                                vendor_id: id || ""
                            }))}
                            required
                            maxItems={1}
                            options={vendors}
                            placeholder="-"
                            errorText="A vendor must be chosen inorder to get the list of unpaid bills."
                        />
                    }

                    {(action === "edit") &&
                        <Control value={details.vendor.title} readOnly />
                    }

                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Vendor Receipt No</Label>
                    <Control
                        value={details.receipt_no}
                        onChange={e => setDetails({ ...details, receipt_no: e.currentTarget.value })}
                        required
                    />
                </Col>
            </Row>
            <Row className="g-2">
                <Col sm={5} className="my-1">
                    <Label className="form-field-title">Payment Date</Label>
                    <DateSelector
                        value={details.payment_date}
                        onChange={val => setDetails(d => ({ ...d, payment_date: val }))}
                        required={true}
                        minDate={(profile.permission_level === 1) ? false : moment().subtract(1, 'M').format("YYYY-MM-DD")}
                        maxDate={0}
                    />
                    <Feedback type="invalid">
                        The payment date should be defined.
                    </Feedback>
                </Col>
                <Col sm={3} xs={6} className="my-1">
                    <Label className="form-field-title">Payment Method</Label>
                    <Form.Select
                        as="select"
                        value={details.method}
                        onChange={e => setDetails({ ...details, method: e.currentTarget.value })}
                        required
                    >
                        <option value=""></option>
                        {paymentMethods.map(c => <option key={c} value={c}>{UCWords(c.replace(/-/g, " "))}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        You must select a method for payment used for this transaction.
                    </Feedback>
                </Col>
                <Col sm={4} xs={6} className="my-1">
                    <Label className="form-field-title">Account</Label>
                    <Form.Select
                        as="select"
                        value={details.account_id}
                        onChange={e => setDetails({ ...details, account_id: e.currentTarget.value })}
                        required
                    >
                        <option value=""></option>
                        {accounts.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        You must select the account this payment went into.
                    </Feedback>
                </Col>
            </Row>



            <h4 className="form-section-label">Bills</h4>

            <Row className="d-none d-sm-flex">
                <Col sm={5}><Label className="form-field-title">Bill</Label></Col>
                <Col sm={3}><Label className="form-field-title">Balance</Label></Col>
                <Col sm={4}><Label className="form-field-title">Amount Paid</Label></Col>
            </Row>

            {prods.map((p, idx) => (
                <Row key={idx} className="g-2 my-3 my-sm-0 pb-3 pb-sm-0">
                    <Col sm={5} xs={12}>
                        <Label className="form-field-title d-sm-none">Bill</Label>
                        <Control
                            readOnly
                            value={p.bill_reference}
                            size="sm"
                        />

                    </Col>
                    <Col sm={3} xs={5}>
                        <Label className="form-field-title d-sm-none">Balance</Label>
                        <Control
                            readOnly
                            value={cur(p.bill_balance, 2).format()}
                            size="sm"
                        />

                    </Col>
                    <Col sm={4} xs={7}>
                        <Label className="form-field-title d-sm-none">Amount Paid</Label>
                        <InputGroup>
                            <Control
                                type="number"
                                value={p.amount}
                                min={500}
                                size="sm"
                                max={Math.ceil(p.bill_balance)}
                                onChange={e => changeProdAmount(e.currentTarget.value, idx)}
                                step={0.01}
                            />
                            <Button variant="link" size="sm" className='text-danger' onClick={() => deleteProd(idx)}>
                                <i className="fas fa-times" />
                            </Button>
                        </InputGroup>
                        <Control.Feedback type="invalid">
                            Amount must be less than {cur(p.bill_balance, 0).format()} but more than 500
                        </Control.Feedback>
                    </Col>
                </Row>

            ))}

            {(display_bills.length > 0) &&
                <Row>
                    <Col className='my-2'>
                        <Select
                            items={display_bills}
                            size="sm"
                            className='rounded-pill'
                            maxItems={10}
                            onSelect={handleBillSelect}
                        >
                            <i className='fas fa-circle-plus me-1' />Add Bills(s)
                        </Select>
                    </Col>
                    <Col className='my-2 text-end h4'>
                        {UGX(prods.reduce((p, c) => p + parseFloat(c.amount), 0)).format()}
                    </Col>
                </Row>
            }

            <Row>
                <Col sm={6} className="my-2">
                    <Label className="form-field-title">Vendor Receipt</Label>
                    <FileInput
                        placeholder='-'
                        maxFiles={1}
                        onChange={([file]) => setDetails(d => ({ ...d, receipt_file_id: file || "" }))}
                        value={[details.receipt_file_id]}
                    />
                </Col>
            </Row>



            <Row>
                <Col className="mt-4 mb-3 text-end">
                    <SubmitButton isSubmitting={isSubmitting} type="submit">
                        {action === "edit" ? "Update Payment" : "Create Payment"}
                    </SubmitButton>
                    <CancelButton isSubmitting={isSubmitting} onClick={() => navigate(-1)}>
                        Cancel
                    </CancelButton>
                </Col>
            </Row>
        </Form>
    )
}


export default PaymentForm;